import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAppStore } from '@/modules/shared/store/appStore';
import { getAccessToken } from '../../utils/helper';
import { hasSession, resolveLandingPath, type SessionAffiliate } from './authGuard';

interface RouteWrapperProps {
  children: ReactNode;
}

/**
 * PublicRoute - A wrapper component for public routes
 * Redirects to dashboard if user is already authenticated
 *
 * Honours `state.from` — the destination `PrivateRoute` was guarding when it bounced the user here.
 * Previously this always sent the user to `DEFAULT_LANDING`, so a deep link that arrived before the
 * affiliate had loaded was lost and the user ended up on create-leads instead.
 *
 * The affiliate comes from the selector hook, not `useAppStore.getState()`, so the redirect fires as
 * soon as the affiliate lands rather than waiting for an unrelated re-render.
 */
const PublicRoute = ({ children }: RouteWrapperProps) => {
  const affiliate = useAppStore((state) => state.affiliate) as SessionAffiliate | null;
  const location = useLocation();

  // `/logout` is no longer handled here: main's `LogoutRoute` owns that route and calls
  // `performLogout()` in an effect, so the work does not re-fire on every render.
  if (!hasSession(getAccessToken(), affiliate)) {
    return <>{children}</>;
  }

  const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  return <Navigate to={resolveLandingPath(from)} replace />;
};

export default PublicRoute;
